import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import moment from "moment";
import i18next from "i18next";

import SharedButton from "../../components/Shared/SharedButton";
import { setInitialSalesfilter } from "../../store/slices/InvoicesSlice";
import InvoiceTable from "./InvoiveTable";
import InvoiceFilters from "./InvoiceFilters";
import FilterDetails from "./FilterDetails";
import ExportInvoices from "./ExportInvoices";
import SearchField from "../../components/Shared/SearchField";
import useFetchData from "../../hooks/useFetchData";
import useDebounce from "../../hooks/useDebounce";

export default function InvoicesMain() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { initialSalesFilters } = useSelector((state) => state.invoice);
  const {
    data: invoicesData,
    loading: isLoading,
    executeFetch,
  } = useFetchData();

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchQuery = useDebounce(searchValue, 500);

  const currentDate = moment().format("YYYY-MM-DD");
  const firstDayOfCurrentMonth = moment().startOf("month").format("YYYY-MM-DD");
  const lang = i18next.language;

  const [filters, setFilters] = useState(
    initialSalesFilters || {
      start_date: firstDayOfCurrentMonth,
      end_date: currentDate,
      branch_id: "",
      customer_id: "",
      customer_name: "",
      invoice_type: "sales",
      payment_method: "",
      page: 1,
      per_page: 10,
    },
  );

  const exportColumnsData = [
    { key: "no", header: t("no") },
    { key: "date", header: t("date") },
    { key: "invoice_number", header: t("invoiceNumber") },
    { key: "file_number", header: t("fileNumber") },
    { key: "customer_name", header: `${t("Customer")} ${t("name")}` },
    {
      key: "patient_share",
      header: `${t("Customer")} ${t("amount")}`,
      type: Number,
    },
    { key: "discount", header: t("discount"), type: Number },
    {
      key: "patient_tax",
      header: `${t("Customer")} ${t("vat")}`,
      type: Number,
    },
    { key: "amount", header: t("total_customer_amount"), type: Number },
    { key: "company_share", header: t("company_amount"), type: Number },
    { key: "company_tax", header: t("company_vat"), type: Number },
    {
      key: "total_company_amount",
      header: t("total_company_amount"),
      type: Number,
      field: "company_share",
    },
    { key: "payment_method", header: t("payment_method") },
    { key: "nationality", header: t("nationality") },
    { key: "paid", header: t("status") },
    { key: "description", header: t("description") },
  ];

  const translations = {
    en: {
      date: "Date",
      invoiceNumber: "Invoice Number",
      fileNumber: "File Number",
      customerName: "Customer Name",
      patientShare: "Customer Amount",
      discount: "Discount",
      patientTax: "Customer Vat",
      amount: "Total Customer Amount",
      companyShare: "Company Share",
      companyTax: "Company Tax",
      totalCompanyAmount: "Total Company Amount",
      paymentMethod: "Payment Method",
      nationality: "Nationality",
      status: "Status",
      description: "Description",
    },
    ar: {
      date: "تاريخ",
      invoiceNumber: "رقم الفاتورة",
      fileNumber: "رقم الملف",
      customerName: "اسم العميل",
      patientShare: "مبلغ العميل",
      discount: "خصم",
      patientTax: "ضريبة العميل",
      amount: "إجمالي مبلغ العميل",
      companyShare: "حصة الشركة",
      companyTax: "ضريبة الشركة",
      totalCompanyAmount: "إجمالي مبلغ الشركة",
      paymentMethod: "طريقة الدفع",
      nationality: "الجنسية",
      status: "الحالة",
      description: "الوصف",
    },
  };

  const exportintoExcelColumns = (data) => {
    const keys = translations[lang] || translations.en;
    const type = (value) => (lang === "ar" ? Number(value) : String(value));
    return data?.map((item) => ({
      [keys.date]: moment(item?.date).format("DD-MM-YYYY HH:mm:ss"),
      [keys.invoiceNumber]: type(item?.invoice_number),
      [keys.fileNumber]: type(item?.file_number),
      [keys.customerName]: item?.customer_name,
      [keys.patientShare]: type(item?.patient_share),
      [keys.patientTax]: type(item?.patient_tax),
      [keys.amount]: type(item?.amount),
      [keys.companyShare]: type(item?.company_share),
      [keys.companyTax]: type(item?.company_tax),
      [keys.totalCompanyAmount]: type(item?.company_share + item?.company_tax),
      [keys.discount]: type(item?.discount),
      [keys.paymentMethod]: item?.payment_method,
      [keys.nationality]: item?.nationality,
      [keys.status]: item?.status,
      [keys.description]: item?.description,
    }));
  };

  const getPayload = useCallback(
    (customFilters = filters) => {
      return {
        ...customFilters,
        query: searchValue,
        branch_id: customFilters.branch_id
          ? Number(customFilters.branch_id)
          : "",
        customer_id: Number(customFilters.customer_id?.value) || 0,
        customer_name: customFilters.customer_id?.label || "",
      };
    },
    [filters, searchValue],
  );

  useEffect(() => {
    const payload = initialSalesFilters
      ? {
          ...initialSalesFilters,
          customer_id: Number(initialSalesFilters.customer_id) || 0,
          customer_name: initialSalesFilters.customer_name || "",
          query: searchValue,
        }
      : getPayload();
    executeFetch("POST", "filterInvoices", payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeFetch, debouncedSearchQuery]);

  const handleSearch = (event) => {
    setSearchValue(event?.target?.value);
  };

  const handleClearSearch = () => {
    setSearchValue("");
  };

  const handleAddFilters = async () => {
    if (filters?.start_date && !filters?.end_date) {
      toast.error("Please select an end date");
      return;
    }
    if (filters?.end_date && !filters?.start_date) {
      toast.error("Please select a start date");
      return;
    }
    const payload = { ...getPayload(), page: 1 };
    executeFetch("POST", "filterInvoices", payload);
    dispatch(setInitialSalesfilter(payload));
  };

  const handleResetFilters = () => {
    setSearchValue("");
    const resetPayload = {
      start_date: firstDayOfCurrentMonth,
      end_date: currentDate,
      branch_id: "",
      customer_id: 0,
      customer_name: "",
      invoice_type: "sales",
      payment_method: "",
      page: 1,
      per_page: 10,
    };
    executeFetch("POST", "filterInvoices", resetPayload);
    dispatch(setInitialSalesfilter(null));
    setFilters(resetPayload);
  };

  const handlePageChange = (newPage, newRowsPerPage) => {
    const updatedFilters = {
      ...filters,
      customer_id: Number(filters.customer_id) || 0,
      page: newPage,
      per_page: newRowsPerPage,
    };
    setFilters(updatedFilters);
    executeFetch("POST", "filterInvoices", getPayload(updatedFilters));
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between mb-2 gap-3">
        <div
          className={`flex items-center gap-2 ${isRTL ? "mr-auto" : "ml-auto"}`}
        >
          <div className="flex gap-2">
            <SearchField
              className="w-full md:!w-[300px]"
              placeholder={t(" Search by invoice number")}
              value={searchValue}
              onChange={handleSearch}
              handleClearClick={handleClearSearch}
            />
          </div>
          <SharedButton
            label={t("CREATESALESINVOICES")}
            icon="pi pi-plus"
            onClick={() => navigate("/manager/invoices/create")}
            className="bg-primary primary-btn capitalize"
          />
          <ExportInvoices
            filters={filters}
            columnsData={exportColumnsData}
            apiUrl="/exportInvoices"
            exportOptionsFormat={exportintoExcelColumns}
            name="Sales Invoices"
          />
        </div>
      </div>
      <InvoiceFilters
        filters={filters}
        setFilters={setFilters}
        filterLoading={isLoading}
        handleAddFilters={handleAddFilters}
        handleResetFilters={handleResetFilters}
      />
      <InvoiceTable
        invoicesList={invoicesData}
        loading={isLoading}
        onPageChange={handlePageChange}
      />
      <FilterDetails filtersDetails={invoicesData} loading={isLoading} />
    </div>
  );
}
